// Portfolio (R-PTF-01 à R-PTF-07) : projets livrés, dépôts, captures, et surtout la forme
// PUBLIQUE de chaque livrable — l'étude de cas. Le marché paie des preuves, pas des titres
// (docs/formation-product-architect.md §8).
import { useState } from 'react';
import { useMagasin, magasin } from '../donnees/magasin';
import { api, ErreurApi } from '../api';
import type { EtudeCas, Projet } from '@partage/types';

const CHECKLIST = [
  { cle: 'secrets', texte: 'Aucun secret : clé d’API, mot de passe, jeton, .env, dump de base.' },
  { cle: 'donnees', texte: 'Aucune donnée personnelle, la mienne comme celle de quelqu’un d’autre.' },
  { cle: 'infra', texte: 'Aucune information d’infrastructure : IP, domaine interne, config Traefik, nom de conteneur, port, chemin du VPS.' },
  { cle: 'relu', texte: 'Relu après le dernier commit — et dans l’historique, pas seulement dans la dernière version.' },
] as const;

const MODELE_README = `# <titre>

## Le problème
Qui avait quel problème, et pourquoi il valait la peine d’être résolu.

## Les utilisateurs
Qui s’en sert, dans quel contexte.

## L’architecture
<schéma C4 : contexte, puis conteneurs>

## Les décisions
- <décision> — pourquoi, et ce qu’elle coûte.

## Les résultats
Ce qui marche, mesuré.

## Les coûts
Hébergement, API, temps.

## Ce que je referais autrement
`;

/** Le bloc étude de cas (R-PTF-05, R-PTF-06) : ce qui deviendra public. */
function BlocEtudeCas({ p }: { p: Projet }) {
  const etude: EtudeCas = p.etude_cas ?? { statut: 'brouillon', readme: '', script_demo: '', post: '', checklist: { secrets: false, donnees: false, infra: false, relu: false } };
  const [ouvert, setOuvert] = useState(false);
  const [f, setF] = useState({ readme: etude.readme, script_demo: etude.script_demo, post: etude.post });

  const coches = CHECKLIST.filter(({ cle }) => etude.checklist[cle]).length;
  const complete = coches === CHECKLIST.length;
  const publiee = etude.statut === 'publie';

  const enregistrer = () => magasin.emettre('etude_cas_modifiee', { projet: p.id, ...f });

  return (
    <div style={{ marginTop: '0.8em', borderTop: '1px solid var(--bordure)', paddingTop: '0.6em' }}>
      <div className="ligne">
        <strong>Étude de cas</strong>
        <span className={`badge ${publiee ? 'ok' : 'accent'}`}>
          {publiee ? 'Publiée' : etude.statut === 'pret' ? 'Prête' : 'Brouillon'}
        </span>
        {!publiee ? <span className="badge">Checklist {coches} / {CHECKLIST.length}</span> : null}
        <span className="espace" />
        <button className="discret petit" onClick={() => setOuvert((o) => !o)}>{ouvert ? 'Replier' : 'Déplier'}</button>
      </div>

      {publiee ? (
        <p className="petit-texte doux">Publiée le {etude.publiee_le?.slice(0, 10)}. {p.depot ? <a href={p.depot} target="_blank" rel="noopener">Voir le dépôt</a> : null}</p>
      ) : null}

      {ouvert ? (
        <>
          <div className="champ">
            <label>README (problème → utilisateurs → architecture → décisions → résultats → coûts)</label>
            <textarea rows={10} value={f.readme} onChange={(e) => setF({ ...f, readme: e.target.value })} onBlur={() => void enregistrer()} placeholder={MODELE_README} />
            {!f.readme ? <button className="discret petit" onClick={() => { setF({ ...f, readme: MODELE_README }); }}>Partir du modèle</button> : null}
          </div>
          <div className="champ">
            <label>Script de la démo vidéo (3 minutes)</label>
            <textarea rows={5} value={f.script_demo} onChange={(e) => setF({ ...f, script_demo: e.target.value })} onBlur={() => void enregistrer()} />
          </div>
          <div className="champ">
            <label>Brouillon du post LinkedIn — tu le relis et tu le publies toi-même</label>
            <textarea rows={4} value={f.post} onChange={(e) => setF({ ...f, post: e.target.value })} onBlur={() => void enregistrer()} />
          </div>

          <fieldset style={{ marginTop: '0.6em' }}>
            <legend className="petit-texte">Avant de publier — les quatre, sans exception</legend>
            {CHECKLIST.map(({ cle, texte }) => (
              <label key={cle} className="petit-texte" style={{ display: 'block', margin: '0.3em 0' }}>
                <input
                  type="checkbox"
                  checked={etude.checklist[cle]}
                  disabled={publiee}
                  onChange={(e) => void magasin.emettre('etude_cas_modifiee', { projet: p.id, checklist: { [cle]: e.target.checked } })}
                />{' '}
                {texte}
              </label>
            ))}
          </fieldset>

          <div className="ligne" style={{ marginTop: '0.6em' }}>
            <button className="petit" onClick={() => void magasin.emettre('etude_cas_modifiee', { projet: p.id, statut: 'pret' })} disabled={publiee || etude.statut === 'pret'}>Marquer prête</button>
            <button
              className="petit principal"
              disabled={publiee || !complete}
              title={complete ? undefined : 'Coche les quatre points de la checklist d’abord'}
              onClick={() => void magasin.emettre('etude_cas_publiee', { projet: p.id })}
            >
              Marquer publiée
            </button>
            <button className="discret petit" onClick={() => void navigator.clipboard.writeText(f.readme)}>Copier le README</button>
          </div>
          {!complete && !publiee ? (
            <p className="petit-texte doux">
              Il manque : {CHECKLIST.filter(({ cle }) => !etude.checklist[cle]).map(({ cle }) => cle).join(', ')}.
              Un secret publié ne se dépublie pas.
            </p>
          ) : null}
        </>
      ) : null}
    </div>
  );
}

function Fiche({ p }: { p: Projet }) {
  const { connecte } = useMagasin();
  const [edition, setEdition] = useState(false);
  const [f, setF] = useState({ titre: p.titre, description: p.description, depot: p.depot ?? '', demo: p.demo ?? '' });
  const [erreur, setErreur] = useState<string>();

  async function enregistrer() {
    try {
      await magasin.emettre('projet_modifie', { projet: p.id, titre: f.titre, description: f.description, depot: f.depot, demo: f.demo });
      setEdition(false);
    } catch (e) { setErreur((e as Error).message); }
  }

  async function envoyerCapture(fichier: File) {
    setErreur(undefined);
    try {
      const fd = new FormData();
      fd.append('fichier', fichier);
      const r = await api<{ id: string }>('/api/fichiers', { formulaire: fd });
      await magasin.emettre('capture_ajoutee', { projet: p.id, fichier: r.id });
    } catch (e) { setErreur(e instanceof ErreurApi ? e.message : 'Envoi impossible hors ligne.'); }
  }

  const exportMarkdown = () => {
    const md = p.etude_cas?.readme
      || `# ${p.titre}\n\n${p.description}\n\n${p.depot ? `- Dépôt : ${p.depot}\n` : ''}${p.demo ? `- Démo : ${p.demo}\n` : ''}- ${p.parcours ? `Parcours ${p.parcours}` : `Saison ${p.saison}`} — ${p.statut === 'livre' ? 'livré' : 'en cours'}\n`;
    void navigator.clipboard.writeText(md);
  };

  return (
    <div className="carte">
      {erreur ? <div className="message erreur">{erreur}</div> : null}
      {edition ? (
        <>
          <div className="champ"><label>Titre</label><input value={f.titre} onChange={(e) => setF({ ...f, titre: e.target.value })} /></div>
          <div className="champ"><label>Description</label><textarea rows={3} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} /></div>
          <div className="champ"><label>Dépôt GitHub (URL)</label><input value={f.depot} onChange={(e) => setF({ ...f, depot: e.target.value })} /></div>
          <div className="champ"><label>Démo (URL)</label><input value={f.demo} onChange={(e) => setF({ ...f, demo: e.target.value })} /></div>
          <div className="ligne"><button className="principal" onClick={() => void enregistrer()}>Enregistrer</button><button onClick={() => setEdition(false)}>Annuler</button></div>
        </>
      ) : (
        <>
          <div className="ligne">
            <h3 style={{ margin: 0 }}>{p.titre}</h3>
            <span className={`badge ${p.statut === 'livre' ? 'ok' : 'accent'}`}>{p.statut === 'livre' ? 'Livré' : 'En cours'}</span>
            <span className="badge">{p.parcours ? `Parcours ${p.parcours}` : `Saison ${p.saison}`}</span>
          </div>
          <p style={{ whiteSpace: 'pre-wrap' }}>{p.description || <span className="doux">Pas encore de description.</span>}</p>
          <p className="petit-texte">{p.depot ? <a href={p.depot} target="_blank" rel="noopener">Dépôt</a> : null} {p.demo ? <> · <a href={p.demo} target="_blank" rel="noopener">Démo</a></> : null}</p>
          <div className="captures">
            {p.captures.map((id) => <span key={id}><img src={`/api/fichiers/${id}`} alt="" /><br /><button className="discret petit" onClick={() => void magasin.emettre('capture_retiree', { projet: p.id, fichier: id })}>retirer</button></span>)}
          </div>
          <div className="ligne" style={{ marginTop: '0.6em' }}>
            <button className="petit" onClick={() => setEdition(true)}>Modifier</button>
            {p.statut === 'en_cours' ? <button className="petit principal" onClick={() => void magasin.emettre('projet_modifie', { projet: p.id, statut: 'livre' })}>Marquer livré</button> : null}
            <label className="bouton petit" style={{ cursor: connecte ? 'pointer' : 'not-allowed' }} title={connecte ? 'JPEG, PNG ou WebP, 2 Mo max' : 'Nécessite le réseau'}>
              Ajouter une capture<input type="file" accept="image/jpeg,image/png,image/webp" style={{ display: 'none' }} disabled={!connecte || p.captures.length >= 5} onChange={(e) => { const fichier = e.target.files?.[0]; if (fichier) void envoyerCapture(fichier); e.target.value = ''; }} />
            </label>
            <button className="discret petit" onClick={exportMarkdown}>Copier en Markdown</button>
          </div>
          <BlocEtudeCas p={p} />
        </>
      )}
    </div>
  );
}

export function Portfolio() {
  const { etat } = useMagasin();
  const [titre, setTitre] = useState('');

  // R-PTF-07 : où en sont les études de cas, saison par saison.
  const livrables = etat.portfolio.filter((p) => p.id.startsWith('livrable:'));
  const publiees = livrables.filter((p) => p.etude_cas?.statut === 'publie').length;
  const encours = etat.portfolio.find((p) => p.id === `livrable:S${etat.saison_courante}`);
  const manquant = encours?.etude_cas
    ? CHECKLIST.filter(({ cle }) => !encours.etude_cas!.checklist[cle]).length
    : undefined;

  async function creer() {
    if (!titre.trim()) return;
    await magasin.emettre('projet_cree', { projet: `projet-${crypto.randomUUID().slice(0, 8)}`, titre: titre.trim(), description: '', saison: etat.saison_courante });
    setTitre('');
  }

  return (
    <>
      <h2>Portfolio</h2>
      <p className="doux">
        Chaque livrable de saison devient une étude de cas publique : un dépôt, un README qui raconte
        le problème et les décisions, une démo de trois minutes, un post. C’est ça qu’un client lit —
        pas un intitulé.
      </p>

      {livrables.length ? (
        <div className="carte">
          <div className="ligne">
            <strong>{publiees} étude{publiees > 1 ? 's' : ''} de cas publiée{publiees > 1 ? 's' : ''}</strong>
            <span className="badge">sur {livrables.length} livrable{livrables.length > 1 ? 's' : ''}</span>
          </div>
          {encours && encours.etude_cas?.statut !== 'publie' ? (
            <p className="petit-texte doux">
              Saison en cours : {manquant === undefined ? 'l’étude de cas n’est pas commencée.' : manquant === 0 ? 'la checklist est complète, tu peux publier.' : `${manquant} point${manquant > 1 ? 's' : ''} de checklist à vérifier avant de publier.`}
            </p>
          ) : null}
        </div>
      ) : null}

      {etat.portfolio.length === 0 ? <p className="vide">Le livrable de la saison apparaît ici dès que la saison est activée.</p> : etat.portfolio.map((p) => <Fiche p={p} key={p.id} />)}
      <div className="carte" style={{ marginTop: '1em' }}>
        <div className="ligne"><input placeholder="Nouveau projet libre" value={titre} onChange={(e) => setTitre(e.target.value)} style={{ maxWidth: 360 }} /><button className="principal" onClick={() => void creer()}>Créer</button></div>
      </div>
    </>
  );
}
